import { BlockStack, Button, InlineGrid, Select } from '@shopify/polaris';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { KindOfTimeType, WeekdayEnum } from '@/constants/enum';
import {
  handleSaveShippingWeeklyWorkingDays,
  shippingSettingSelector,
  shippingWeeklyWorkingDaySelector,
} from '@/redux/slice/shipping.slice';
import { ExactTimeType } from '@/types';
import { time24hTo12h } from '@/utils';
import { useDispatch, useSelector } from 'react-redux';
import TimeSlotSection from './TimeSlotSection';
import { IWeeklyWorkingDay } from '@/types/components/day';

const ShippingExactTime = ({
  selectedWorkingDay,
  selectTabDays,
  time24h,
}: {
  selectedWorkingDay: IWeeklyWorkingDay;
  selectTabDays: number;
  time24h: { id: number; value: string }[];
}) => {
  const { t } = useTranslation('shippingSettings');
  const shippingSettings = useSelector(shippingSettingSelector);
  const shippingWeeklyWorkingDays = useSelector(shippingWeeklyWorkingDaySelector);
  const dispatch = useDispatch();

  const [dayToCopy, setDayToCopy] = useState<string>('');

  const copyOptionList = useMemo(() => {
    return [
      { label: t('shippingTime.exactTime.copySection.select.options.monday'), value: WeekdayEnum.MONDAY.toString() },
      { label: t('shippingTime.exactTime.copySection.select.options.tuesday'), value: WeekdayEnum.TUESDAY.toString() },
      { label: t('shippingTime.exactTime.copySection.select.options.wednesday'), value: WeekdayEnum.WEDNESDAY.toString() },
      { label: t('shippingTime.exactTime.copySection.select.options.thursday'), value: WeekdayEnum.THURSDAY.toString() },
      { label: t('shippingTime.exactTime.copySection.select.options.friday'), value: WeekdayEnum.FRIDAY.toString() },
      { label: t('shippingTime.exactTime.copySection.select.options.saturday'), value: WeekdayEnum.SATURDAY.toString() },
      { label: t('shippingTime.exactTime.copySection.select.options.sunday'), value: WeekdayEnum.SUNDAY.toString() },
    ]
      .filter((item) => shippingWeeklyWorkingDays.find((day) => day.day === parseInt(item.value))?.isEnable)
      .filter((item) => item.value !== selectedWorkingDay?.day.toString());
  }, [shippingWeeklyWorkingDays, selectedWorkingDay?.day, t]);

  const timeOptions = useMemo(() => {
    return time24h
      .map((item) => {
        return {
          label: time24hTo12h(item.value),
          value: item.value,
        };
      })
      .filter((item) => !selectedWorkingDay?.exactTimes?.map((item) => item.start)?.includes(item.value));
  }, [selectedWorkingDay?.exactTimes, time24h]);

  const [selectTime, setSelectTime] = useState(timeOptions.length > 0 ? timeOptions[0].value : '');

  useEffect(() => {
    setSelectTime(timeOptions.length > 0 ? timeOptions[0].value : '');
  }, [timeOptions]);

  useEffect(() => {
    setDayToCopy('');
  }, [selectTabDays]);

  const handleCopy = (value: string) => {
    setDayToCopy(value);
    const day = parseInt(value);
    const dayToCopyData = shippingWeeklyWorkingDays.find((item) => item.day === day);

    if (dayToCopyData) {
      dispatch(
        handleSaveShippingWeeklyWorkingDays(
          shippingWeeklyWorkingDays.map((item) => {
            if (item.day === selectedWorkingDay?.day) {
              return {
                ...item,
                exactTimes: dayToCopyData.exactTimes,
              };
            }
            return item;
          }),
        ),
      );
    }
  };

  const handleAddTime = () => {
    const newExactTime: ExactTimeType = {
      start: selectTime,
      orderLimit: 0,
    };
    const newExactTimes = [...selectedWorkingDay?.exactTimes, newExactTime];

    dispatch(
      handleSaveShippingWeeklyWorkingDays(
        shippingWeeklyWorkingDays.map((item) => {
          if (item.day === selectedWorkingDay?.day) {
            return {
              ...item,
              exactTimes: newExactTimes,
            };
          }
          return item;
        }),
      ),
    );
  };

  return (
    <BlockStack gap={'400'}>
      {shippingSettings.shippingTimeSlotType === KindOfTimeType.DIFFERENT && (
        <InlineGrid columns={'1fr auto'} gap="200" alignItems="end">
          <BlockStack>
            <Select
              label="Copy setup from"
              placeholder="Select one"
              options={copyOptionList}
              onChange={(value) => handleCopy(value)}
              value={dayToCopy}
            />
          </BlockStack>
        </InlineGrid>
      )}
      <InlineGrid columns="1fr auto" gap="200" alignItems="end">
        <BlockStack>
          <Select
            label="Select time"
            placeholder={timeOptions.length === 0 ? t('shippingTime.exactTime.copySection.select.placeHolder') : ''}
            options={timeOptions}
            value={selectTime}
            onChange={(value) => setSelectTime(value)}
          />
        </BlockStack>
        <Button onClick={handleAddTime} size="large" disabled={timeOptions.length === 0}>
          Add time
        </Button>
      </InlineGrid>
      {selectedWorkingDay && <TimeSlotSection selectedWorkingDay={selectedWorkingDay} />}
    </BlockStack>
  );
};

export default ShippingExactTime;
